import {
  useAppFabricEnvironment,
  useAppFabricSandbox,
} from '@mc/appfabric/environment';
import { isIntuitDomain } from '@mc/window';
/**
 * See AppFabric Sandbox documentation in [GitHub]{@link https://github.intuit.com/pages/AppFabric/sandbox-spec/interfaces/SandboxPerformance.html#createCustomerInteraction}
 * for full API details.
 *
 * This function is a simple wrapper on AppFabric's implementation that guarantees an object is
 * returned, preventing the need for consumers to implement null checks. If additional functionality
 * is needed, using the AppFabric Performance API directly is a good option.
 *
 * To better understand when/how/why to use this function, you should review
 * [UX AppFabric - RUM]{@link https://devportal.intuit.com/app/dp/capability/74/capabilityDocs/main/docs/rum/concepts/web.md}
 *
 * There's also a dojo copy of this solution in web/js/mojo/appfabric/interactions.js
 * There's a bridge-compatible copy of this solution that uses a bridge-safe version of Bugsnag in web/js/@mc/appfabric/interactionsCA.js
 *
 * @param interactionName
 * @param metaData
 * @param options
 * @param scope - 'plugin' or 'local' - [GitHub]{@link https://github.intuit.com/pages/AppFabric/sandbox-spec/enums/CustomerInteractionScope.html}
 * @returns {{fail: function(string: errorMsg), success: function(), record: function(), abort: function(string: reason?)}}
 */
const createCustomerInteraction = (
  interactionName,
  metaData = {},
  options = {},
  scope = 'plugin',
  environment = 'mailchimp',
  sandbox = null,
) => {
  let appFabricInstance = window.appFabricInstance;

  if (isIntuitDomain()) {
    if (environment === 'appfabric' && sandbox) {
      appFabricInstance = sandbox;
    }
  }
  const instance = appFabricInstance;

  // Always return an object with success(), fail() and record()
  // so that consumers of createCustomerInteraction() do not need to null check
  // and if the creation does fail for some reason it is non-blocking.
  let interaction = {
    success: () => {},
    fail: () => {},
    record: () => {},
    abort: () => {},
    setDegraded: () => {},
  };

  if (instance && instance.performance) {
    try {
      let interactionScope;
      switch (scope) {
        case 'plugin':
          interactionScope =
            instance.performance.CustomerInteractionScope.PLUGIN;
          break;
        case 'local':
          interactionScope =
            instance.performance.CustomerInteractionScope.LOCAL;
          break;
        default:
          interactionScope =
            instance.performance.CustomerInteractionScope.PLUGIN;
          break;
      }

      interaction = instance.performance.createCustomerInteraction(
        interactionName,
        metaData,
        {
          ...options,
          scope: interactionScope,
        },
      );

      interaction.record = () => {
        instance.performance.record(interaction);
      };
    } catch (err) {
      console.error(
        `Failed to create customer interaction: ${interactionName}`,
      );
    }
  }

  return interaction;
};

/**
 * React hook wrapper for createCustomerInteraction that properly handles the environment
 * @param interactionName
 * @param metaData
 * @param options
 * @param scope
 * @returns {{fail: function(string: errorMsg), success: function(), record: function(), abort: function(string: reason?)}}
 */
const useCreateCustomerInteraction = (
  interactionName,
  metaData = {},
  options = {},
  scope = 'plugin',
) => {
  const environment = useAppFabricEnvironment();
  const sandbox = useAppFabricSandbox();

  return createCustomerInteraction(
    interactionName,
    metaData,
    options,
    scope,
    environment,
    sandbox,
  );
};

export { createCustomerInteraction, useCreateCustomerInteraction };
